import React, { useEffect, useState } from "react";
import { Send, Sparkles, Loader2, Download } from "lucide-react";
import { toast } from "sonner";
import { api, aiJob } from "@/lib/api";
import { PageHeader, SectionCard } from "@/components/Primitives";
import DataTable from "@/components/DataTable";
import { AIOutputDisclaimer } from "@/components/Disclaimer";
import AIFeedback from "@/components/AIFeedback";
import ShowReasoning from "@/components/ShowReasoning";
import { exportToPDF } from "@/lib/pdf";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";

export default function PatientEngagement() {
  const [patients, setPatients] = useState([]);
  const [selected, setSelected] = useState([]);
  const [message, setMessage] = useState("Reminder: You have an upcoming appointment.");
  const [noShow, setNoShow] = useState(null);
  const [instr, setInstr] = useState({ patient_id: "", diagnosis: "", cpt: "", text: "", loading: false });

  useEffect(()=>{
    api.get("/engagement/patients").then(r=>{ setPatients(r.data); setInstr(i=>({...i, patient_id: r.data[0]?.id||""})); });
    api.get("/engagement/no-show").then(r=>setNoShow(r.data));
  }, []);

  const toggle = (id) => setSelected(s => s.includes(id) ? s.filter(x=>x!==id) : [...s, id]);

  const send = async () => {
    if (selected.length === 0) return toast.error("Select patients first");
    await api.post("/engagement/reminders", { patient_ids: selected, message });
    toast.success(`Sent to ${selected.length} patients`);
    setSelected([]);
  };

  const genInstr = async () => {
    if (!instr.diagnosis || !instr.cpt) return;
    setInstr({ ...instr, loading: true });
    try {
      const data = await aiJob("/engagement/instructions", { patient_id: instr.patient_id, diagnosis: instr.diagnosis, cpt: instr.cpt });
      setInstr({ ...instr, text: data.final, score: data.score, reasoning: data.reasoning, verification_footer: data.verification_footer, loading: false });
      toast.success("Instructions generated");
    } catch { setInstr({...instr, loading: false}); toast.error("Failed"); }
  };

  const cols = [
    { key: "select", label: "", sortable: false, render: (r) => (
      <input type="checkbox" checked={selected.includes(r.id)} onChange={()=>toggle(r.id)} className="accent-[#4A0E8F]" data-testid={`select-${r.id}`} />
    )},
    { key: "name", label: "Patient" },
    { key: "last_visit", label: "Last Visit", render: (r)=>r.last_visit?new Date(r.last_visit).toLocaleDateString():"—" },
    { key: "next_appointment", label: "Next Appt", render: (r)=>r.next_appointment || "—" },
    { key: "medication_compliance", label: "Compliance", render: (r)=><span className={`status-pill ${r.medication_compliance==="good"?"status-approved":"status-pending"}`}>{r.medication_compliance}</span> },
  ];

  return (
    <>
      <PageHeader eyebrow="Module 9" title="Patient Engagement Hub"
        subtitle="Bulk reminders, post-visit instructions and no-show analytics." testid="engagement-page" />

      <SectionCard title="Patient List" testid="engagement-list"
        actions={
          <div className="flex gap-2 items-center">
            <input value={message} onChange={(e)=>setMessage(e.target.value)} className="px-2 py-1 border border-brand rounded text-xs w-64" />
            <button onClick={send} className="px-3 py-1.5 bg-brand-primary text-white rounded text-xs inline-flex items-center gap-1.5" data-testid="send-reminders-btn">
              <Send className="w-3.5 h-3.5" /> Send to {selected.length}
            </button>
          </div>
        }>
        <DataTable data={patients} columns={cols} searchKeys={["name","insurance_plan"]} testid="engagement-table" />
      </SectionCard>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        <SectionCard title="Post-Visit Instruction Generator"
          actions={instr.text && <button onClick={()=>exportToPDF("Post-Visit Instructions", instr.text)} className="text-xs px-3 py-1.5 rounded-md border border-brand hover:bg-brand-bg inline-flex items-center gap-1.5"><Download className="w-3.5 h-3.5" /> PDF</button>}
          testid="instructions-card">
          <select value={instr.patient_id} onChange={(e)=>setInstr({...instr, patient_id:e.target.value})}
            className="w-full mb-2 px-3 py-2 bg-white border border-brand rounded-md text-sm">
            {patients.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <div className="grid grid-cols-2 gap-2">
            <input placeholder="Diagnosis" value={instr.diagnosis} onChange={(e)=>setInstr({...instr, diagnosis:e.target.value})}
              className="px-3 py-2 bg-white border border-brand rounded-md text-sm" data-testid="instr-diagnosis" />
            <input placeholder="CPT" value={instr.cpt} onChange={(e)=>setInstr({...instr, cpt:e.target.value})}
              className="px-3 py-2 bg-white border border-brand rounded-md text-sm" data-testid="instr-cpt" />
          </div>
          <button onClick={genInstr} disabled={instr.loading} className="mt-2 bg-brand-primary text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-[#35096B] inline-flex items-center gap-2 disabled:opacity-50" data-testid="gen-instr-btn">
            {instr.loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Generate
          </button>
          {instr.text && (
            <div className="mt-4">
              <AIOutputDisclaimer />
              <pre className="text-xs whitespace-pre-wrap leading-relaxed bg-brand-bg p-3 rounded-md max-h-72 overflow-y-auto" data-testid="instr-text">{instr.text}</pre>
              {instr.verification_footer && (
                <div className="mt-3 px-3 py-2 rounded-md bg-amber-50 border border-amber-300 text-xs text-amber-900 italic" data-testid="instr-verification-footer">{instr.verification_footer}</div>
              )}
              <ShowReasoning reasoning={instr.reasoning} score={instr.score} testid="instr-reasoning" />
              <AIFeedback module="patient-engagement" outputType="post_visit_instructions" testid="instr-feedback" />
            </div>
          )}
        </SectionCard>

        <SectionCard title="No-Show Analytics" testid="no-show">
          {noShow && (
            <>
              <div className="text-3xl font-semibold text-brand-primary">{noShow.overall_rate}%</div>
              <div className="text-xs text-brand-muted mb-4">Overall no-show rate</div>
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={noShow.by_day_of_week}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E9E3F5" />
                  <XAxis dataKey="day" tick={{ fontSize: 11, fill: "#6E6380" }} />
                  <YAxis tick={{ fontSize: 11, fill: "#6E6380" }} />
                  <Tooltip cursor={{ fill: "#F8F4FF" }} />
                  <Bar dataKey="rate" fill="#4A0E8F" radius={[6,6,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            </>
          )}
        </SectionCard>
      </div>
    </>
  );
}
